import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { timeLeadsAPI } from '../services/api';
import { CreateTimeActivity, TimeActivity } from '../types';
import TableLoadingRow from './TableLoadingRow';
import { todayInputValue, toDateInputValue } from './LeadForm';
import { formatDdMmYyyy, isValidGregorianInputDate, utcDayFromIsoInstant } from '../utils/activityDates';

interface ActivityFormState {
  title: string;
  date: string;
}

const emptyForm = (): ActivityFormState => ({ title: '', date: todayInputValue() });

const buildCreatePayload = (form: ActivityFormState): CreateTimeActivity => ({
  title: form.title.trim(),
  // Dual-write the canonical business date and the legacy alias so the same
  // payload works against old and new API versions during rollout skew.
  activityDate: form.date,
  createdDate: form.date,
});

// Edit forms prefer the canonical activityDate; the legacy createdDate date
// prefix is only a version-skew fallback for responses from older APIs.
const editFormDate = (activity: TimeActivity): string =>
  activity.activityDate || toDateInputValue(activity.createdDate);

const ACTIVITY_DATE_ERROR = 'Enter a valid activity date';

interface LeadActivitiesProps {
  leadId: number;
  onActivitiesChanged?: () => void;
  inlineForm?: boolean;
}

// Project activities for a lead. A lead is a shared Flow project, so the
// activities are stored against the project with ReferenceType "Project".
const LeadActivities: React.FC<LeadActivitiesProps> = ({ leadId, onActivitiesChanged, inlineForm = false }) => {
  const [activities, setActivities] = useState<TimeActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isFormOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ActivityFormState>(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await timeLeadsAPI.getActivities(leadId);
      setActivities(data ?? []);
    } catch (error) {
      console.error('[FIX:leads] Failed to load lead activities', error);
      setLoadError('Activities could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
    setFormError('');
    setFormOpen(false);
  };

  const openCreateForm = () => {
    // Recompute the UTC "today" default each time a new form opens.
    setForm(emptyForm());
    setEditingId(null);
    setFormError('');
    setFormOpen(true);
  };

  const openEditForm = (activity: TimeActivity) => {
    setForm({ title: activity.title, date: editFormDate(activity) });
    setEditingId(activity.id);
    setFormError('');
    setFormOpen(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;

    if (!form.title.trim()) {
      setFormError('Title is required');
      return;
    }

    if (!form.date) {
      setFormError('Date is required');
      return;
    }

    if (!isValidGregorianInputDate(form.date)) {
      setFormError(ACTIVITY_DATE_ERROR);
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      if (editingId) {
        await timeLeadsAPI.updateActivity(leadId, editingId, {
          ...buildCreatePayload(form),
          id: editingId,
        });
        toast.success('Activity updated');
      } else {
        await timeLeadsAPI.createActivity(leadId, buildCreatePayload(form));
        toast.success('Activity created');
      }
      resetForm();
      onActivitiesChanged?.();
      await loadActivities();
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Activity could not be saved.';
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const deleteActivity = async (activity: TimeActivity) => {
    try {
      await timeLeadsAPI.deleteActivity(leadId, activity.id);
      setActivities(current => current.filter(item => item.id !== activity.id));
      toast.success('Activity deleted');
      onActivitiesChanged?.();
    } catch (error) {
      console.error('[FIX:leads] Failed to delete lead activity', error);
      toast.error('Activity could not be deleted');
    }
  };

  const renderCreatedDateCell = (activity: TimeActivity) => {
    if (activity.createdAtUtc === undefined || activity.createdAtUtc === null) {
      return 'Unknown';
    }

    const day = utcDayFromIsoInstant(activity.createdAtUtc);
    if (day === null) {
      // A non-null timestamp that is not an unambiguous UTC instant is an
      // invalid response — surface a diagnostic rather than guessing.
      console.warn('[FIX:leads] Activity returned an invalid createdAtUtc value', {
        id: activity.id,
        createdAtUtc: activity.createdAtUtc,
      });
      return 'Invalid creation timestamp';
    }

    return (
      <details className="created-date-details">
        {/* Keep native disclosure semantics, including its expanded state. */}
        <summary>{day}</summary>
        <div className="created-date-exact">{activity.createdAtUtc}</div>
      </details>
    );
  };

  return (
    <section className="card card-section" aria-label="Lead activities">
      <div className="card-header">
        <h2 className="card-title">Activities</h2>
        <button type="button" className="btn btn-primary" onClick={openCreateForm}>
          Add activity
        </button>
      </div>
      <div className="card-body">
        {loadError && (
          <div role="alert" className="alert alert-danger">
            {loadError}
            <button type="button" className="btn btn-secondary" onClick={loadActivities}>
              Retry
            </button>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover" aria-label="Lead activities">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Activity date</th>
                <th scope="col">Created date</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <TableLoadingRow colSpan={4} label="Loading activities..." />}
              {!loading && activities.map(activity => (
                <tr key={activity.id}>
                  <td data-label="Title">{activity.title}</td>
                  <td data-label="Activity date">{formatDdMmYyyy(editFormDate(activity))}</td>
                  <td data-label="Created date">{renderCreatedDateCell(activity)}</td>
                  <td data-label="Actions">
                    <div className="action-row">
                      <button
                        type="button"
                        className="action-btn"
                        onClick={() => openEditForm(activity)}
                        title="Edit"
                        aria-label={`Edit ${activity.title}`}
                      >
                        <i className="fa-solid fa-pen" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="action-btn danger"
                        onClick={() => deleteActivity(activity)}
                        title="Delete"
                        aria-label={`Delete ${activity.title}`}
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && activities.length === 0 && (
                <tr>
                  <td colSpan={4}><div className="empty-state">No activities yet.</div></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isFormOpen && (
        <div
          className={inlineForm ? undefined : 'time-modal-backdrop'}
          onMouseDown={(event) => {
            if (!inlineForm && event.target === event.currentTarget) {
              resetForm();
            }
          }}
        >
          <section
            className={inlineForm ? 'card card-section' : 'time-modal'}
            role={inlineForm ? undefined : 'dialog'}
            aria-modal={inlineForm ? undefined : true}
            aria-labelledby="lead-activity-dialog-title"
            onKeyDown={event => {
              if (event.key === 'Escape' && inlineForm) {
                event.stopPropagation();
                resetForm();
              }
            }}
          >
            <div className="time-modal-header">
              <h5 id="lead-activity-dialog-title" className="card-title">
                {editingId ? 'Edit activity' : 'New activity'}
              </h5>
              <button className="icon-button" type="button" onClick={resetForm} aria-label="Close activity form">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              <form className="time-form" onSubmit={handleSubmit} noValidate>
                {formError && (
                  <div role="alert" className="alert alert-danger" id="lead-activity-error">
                    {formError}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label" htmlFor="lead-activity-title">
                    Title <span className="required-mark">*</span>
                  </label>
                  <input
                    id="lead-activity-title"
                    className="form-control"
                    value={form.title}
                    maxLength={256}
                    aria-invalid={formError === 'Title is required' ? true : undefined}
                    aria-describedby={formError === 'Title is required' ? 'lead-activity-error' : undefined}
                    onChange={event => {
                      setForm(current => ({ ...current, title: event.target.value }));
                      if (formError) setFormError('');
                    }}
                    disabled={saving}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="lead-activity-date">
                    Activity date <span className="required-mark">*</span>
                  </label>
                  <input
                    id="lead-activity-date"
                    type="date"
                    className="form-control"
                    min="0001-01-01"
                    max="9999-12-31"
                    value={form.date}
                    aria-invalid={formError === 'Date is required' || formError === ACTIVITY_DATE_ERROR ? true : undefined}
                    aria-describedby={formError === 'Date is required' || formError === ACTIVITY_DATE_ERROR ? 'lead-activity-error' : undefined}
                    onChange={event => {
                      setForm(current => ({ ...current, date: event.target.value }));
                      if (formError) setFormError('');
                    }}
                    disabled={saving}
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : editingId ? 'Update activity' : 'Create activity'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={saving}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>
      )}
    </section>
  );
};

export default LeadActivities;
